import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

type AlertTone = 'info' | 'success' | 'warning' | 'error';

type AlertPayload = {
  title?: string;
  message: string;
  tone?: AlertTone;
};

type AppAlertContextValue = {
  showAlert: (payload: AlertPayload | string) => void;
  showConfirm: (payload: { title?: string; message: string; confirmText?: string; cancelText?: string; tone?: AlertTone }) => Promise<boolean>;
};

const AppAlertContext = createContext<AppAlertContextValue | null>(null);

const toneConfig: Record<AlertTone, { icon: JSX.Element; title: string; iconClass: string }> = {
  info: {
    icon: <Info className="h-5 w-5" />,
    title: 'Bilgilendirme',
    iconClass: 'bg-blue-100 text-blue-600',
  },
  success: {
    icon: <CheckCircle2 className="h-5 w-5" />,
    title: 'İşlem Başarılı',
    iconClass: 'bg-emerald-100 text-emerald-600',
  },
  warning: {
    icon: <TriangleAlert className="h-5 w-5" />,
    title: 'Dikkat',
    iconClass: 'bg-amber-100 text-amber-600',
  },
  error: {
    icon: <AlertCircle className="h-5 w-5" />,
    title: 'Hata',
    iconClass: 'bg-red-100 text-red-600',
  },
};

export function AppAlertProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [alertState, setAlertState] = useState<AlertPayload>({ message: '', tone: 'info' });
  const [confirmState, setConfirmState] = useState<{ open: boolean; title: string; message: string; confirmText: string; cancelText: string; tone: AlertTone; resolver: ((value: boolean) => void) | null }>({
    open: false,
    title: 'Onay Gerekli',
    message: '',
    confirmText: 'Onayla',
    cancelText: 'Vazgeç',
    tone: 'warning',
    resolver: null,
  });
  const nativeAlertRef = useRef<typeof window.alert | null>(null);

  const showAlert = (payload: AlertPayload | string) => {
    const normalized = typeof payload === 'string'
      ? { message: payload, tone: 'info' as AlertTone }
      : { tone: 'info' as AlertTone, ...payload };

    const nextTone = normalized.tone || 'info';
    setAlertState({
      title: normalized.title || toneConfig[nextTone].title,
      message: normalized.message,
      tone: nextTone,
    });
    setOpen(true);
  };

  const showConfirm = (payload: { title?: string; message: string; confirmText?: string; cancelText?: string; tone?: AlertTone }) => {
    return new Promise<boolean>((resolve) => {
      const tone = payload.tone || 'warning';
      setConfirmState({
        open: true,
        title: payload.title || 'Onay Gerekli',
        message: payload.message,
        confirmText: payload.confirmText || 'Onayla',
        cancelText: payload.cancelText || 'Vazgeç',
        tone,
        resolver: resolve,
      });
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    nativeAlertRef.current = window.alert.bind(window);
    window.alert = (message?: unknown) => {
      const text = typeof message === 'string' ? message : String(message ?? '');
      showAlert({ message: text, tone: 'info' });
    };

    return () => {
      if (nativeAlertRef.current) {
        window.alert = nativeAlertRef.current;
      }
    };
  }, []);

  const value = useMemo(() => ({ showAlert, showConfirm }), []);
  const currentTone = alertState.tone || 'info';
  const currentToneConfig = toneConfig[currentTone];
  const currentConfirmToneConfig = toneConfig[confirmState.tone || 'warning'];

  return (
    <AppAlertContext.Provider value={value}>
      {children}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl border-slate-200 p-0 overflow-hidden">
          <div className="p-6">
            <DialogHeader className="text-left">
              <div className="flex items-start gap-4">
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${currentToneConfig.iconClass}`}>
                  {currentToneConfig.icon}
                </div>
                <div>
                  <DialogTitle className="text-xl font-semibold text-slate-900">{alertState.title}</DialogTitle>
                  <DialogDescription className="mt-2 text-sm leading-6 text-slate-600 whitespace-pre-wrap">
                    {alertState.message}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <DialogFooter className="mt-6 sm:justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Tamam
              </button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmState.open} onOpenChange={(nextOpen) => {
        if (!nextOpen && confirmState.open) {
          confirmState.resolver?.(false);
          setConfirmState((current) => ({ ...current, open: false, resolver: null }));
        }
      }}>
        <DialogContent className="max-w-md rounded-2xl border-slate-200 p-0 overflow-hidden">
          <div className="p-6">
            <DialogHeader className="text-left">
              <div className="flex items-start gap-4">
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${currentConfirmToneConfig.iconClass}`}>
                  {currentConfirmToneConfig.icon}
                </div>
                <div>
                  <DialogTitle className="text-xl font-semibold text-slate-900">{confirmState.title}</DialogTitle>
                  <DialogDescription className="mt-2 text-sm leading-6 text-slate-600 whitespace-pre-wrap">
                    {confirmState.message}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <DialogFooter className="mt-6 sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  confirmState.resolver?.(false);
                  setConfirmState((current) => ({ ...current, open: false, resolver: null }));
                }}
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {confirmState.cancelText}
              </button>
              <button
                type="button"
                onClick={() => {
                  confirmState.resolver?.(true);
                  setConfirmState((current) => ({ ...current, open: false, resolver: null }));
                }}
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                {confirmState.confirmText}
              </button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </AppAlertContext.Provider>
  );
}

export function useAppAlert() {
  const context = useContext(AppAlertContext);
  if (!context) {
    throw new Error('useAppAlert must be used within AppAlertProvider');
  }
  return context;
}
